import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Plus, Edit, Trash2, Heart, MapPin, Wrench, CheckCircle, Clock, HelpCircle, AlertTriangle } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { sanitizeFormData } from "@/utils/sanitize";

const iconMap = {
  "heart": Heart,
  "map-pin": MapPin,
  "wrench": Wrench,
  "check-circle": CheckCircle,
  "clock": Clock,
  "help-circle": HelpCircle,
  "alert-triangle": AlertTriangle
};

const faqSchema = z.object({
  category: z.string().min(1, "La catégorie est requise"),
  category_icon: z.string().min(1, "L'icône est requise"),
  question: z.string().min(1, "La question est requise"),
  answer: z.string().min(1, "La réponse est requise"),
  order_index: z.number().min(0, "L'ordre doit être positif"),
  is_active: z.boolean()
});

type FAQ = {
  id: string;
  category: string;
  category_icon: string;
  question: string;
  answer: string;
  order_index: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export const FAQManager = () => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
  const { toast } = useToast();

  const form = useForm<z.infer<typeof faqSchema>>({
    resolver: zodResolver(faqSchema),
    defaultValues: {
      category: "",
      category_icon: "help-circle",
      question: "",
      answer: "",
      order_index: 0,
      is_active: true
    }
  });

  const fetchFAQs = async () => {
    try {
      const { data, error } = await supabase
        .from('faqs')
        .select('*')
        .order('category', { ascending: true })
        .order('order_index', { ascending: true });

      if (error) throw error;
      setFaqs(data || []);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les FAQ",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFAQs();
  }, []);

  const onSubmit = async (values: z.infer<typeof faqSchema>) => {
    try {
      // Sanitize input data to prevent XSS attacks
      const sanitizedValues = sanitizeFormData(values) as z.infer<typeof faqSchema>;
      
      if (editingFaq) {
        // Update existing FAQ
        const { error } = await supabase
          .from('faqs')
          .update(sanitizedValues)
          .eq('id', editingFaq.id);

        if (error) throw error;
        
        toast({
          title: "Succès",
          description: "FAQ mise à jour avec succès"
        });
      } else {
        // Create new FAQ
        const insertData: {
          category: string;
          category_icon: string;
          question: string;
          answer: string;
          order_index: number;
          is_active: boolean;
        } = {
          category: values.category,
          category_icon: values.category_icon,
          question: values.question,
          answer: values.answer,
          order_index: values.order_index,
          is_active: values.is_active
        };
        
        const { error } = await supabase
          .from('faqs')
          .insert(insertData);

        if (error) throw error;
        
        toast({
          title: "Succès",
          description: "FAQ créée avec succès"
        });
      }

      setDialogOpen(false);
      setEditingFaq(null);
      form.reset();
      fetchFAQs();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder la FAQ",
        variant: "destructive"
      });
    }
  };

  const deleteFaq = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette FAQ ?')) return;

    try {
      const { error } = await supabase
        .from('faqs')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Succès",
        description: "FAQ supprimée avec succès"
      });
      
      fetchFAQs();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer la FAQ",
        variant: "destructive"
      });
    }
  };

  const openEditDialog = (faq: FAQ) => {
    setEditingFaq(faq);
    form.reset({
      category: faq.category,
      category_icon: faq.category_icon,
      question: faq.question,
      answer: faq.answer,
      order_index: faq.order_index,
      is_active: faq.is_active
    });
    setDialogOpen(true);
  };

  const openAddDialog = () => {
    setEditingFaq(null);
    form.reset({
      category: "",
      category_icon: "help-circle",
      question: "",
      answer: "",
      order_index: 0,
      is_active: true
    });
    setDialogOpen(true);
  };

  // Group FAQs by category
  const groupedFaqs = faqs.reduce((acc, faq) => {
    if (!acc[faq.category]) {
      acc[faq.category] = [];
    }
    acc[faq.category].push(faq);
    return acc;
  }, {} as Record<string, FAQ[]>);

  if (loading) {
    return <div className="p-6">Chargement des FAQ...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-display font-bold">Gestion des FAQ</h2>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openAddDialog} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Ajouter une FAQ
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingFaq ? 'Modifier la FAQ' : 'Ajouter une FAQ'}
              </DialogTitle>
              <DialogDescription>
                {editingFaq ? 'Modifiez les détails de la FAQ' : 'Créez une nouvelle FAQ'}
              </DialogDescription>
            </DialogHeader>
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Catégorie</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: Réservation & Prix" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="category_icon"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Icône de catégorie</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Choisir une icône" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="heart">❤️ Heart</SelectItem>
                          <SelectItem value="map-pin">📍 Map Pin</SelectItem>
                          <SelectItem value="wrench">🔧 Wrench</SelectItem>
                          <SelectItem value="check-circle">✅ Check Circle</SelectItem>
                          <SelectItem value="clock">🕐 Clock</SelectItem>
                          <SelectItem value="help-circle">❓ Help Circle</SelectItem>
                          <SelectItem value="alert-triangle">⚠️ Alert Triangle</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="question"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Question</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: Quel est le prix de location ?" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="answer"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Réponse</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Réponse détaillée à la question..."
                          className="min-h-[100px]"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="order_index"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Ordre d'affichage</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          placeholder="0" 
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="is_active"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2">
                      <FormControl>
                        <Switch 
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel>FAQ active</FormLabel>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1">
                    {editingFaq ? 'Mettre à jour' : 'Créer'}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setDialogOpen(false)}
                  >
                    Annuler
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-6">
        {Object.entries(groupedFaqs).map(([category, categoryFaqs]) => {
          const IconComponent = iconMap[categoryFaqs[0].category_icon as keyof typeof iconMap] || HelpCircle;
          
          return (
            <Card key={category}>
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center text-white">
                    <IconComponent className="w-4 h-4" />
                  </div>
                  {category}
                  <Badge variant="secondary">{categoryFaqs.length} FAQ{categoryFaqs.length > 1 ? 's' : ''}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {categoryFaqs.map((faq) => (
                    <div key={faq.id} className="border border-border rounded-lg p-4 space-y-3">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold">{faq.question}</h4>
                            {!faq.is_active && <Badge variant="secondary">Inactive</Badge>}
                            <Badge variant="outline">#{faq.order_index}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{faq.answer}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditDialog(faq)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => deleteFaq(faq.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {Object.keys(groupedFaqs).length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <HelpCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Aucune FAQ</h3>
            <p className="text-muted-foreground mb-4">
              Commencez par créer votre première FAQ
            </p>
            <Button onClick={openAddDialog}>
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une FAQ
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};